/**
 * @fileoverview The weather block above the Plants page's garden: Today's
 * weather row, then the week as Today says it, one line per rule line, each
 * a filter for the plants it names (the owner's choice, 2026-10-14: "for
 * the top of the plant page, option 1").
 *
 * The row is Today's own: the reading and the sky (useWeatherNow, the one
 * reading every page shows), then the brief's strip line ("Low 67° tonight ·
 * no rain in the forecast"), the whole row opening Weather; the header's
 * weather pill is hidden on this page, as on Today. Under it, the week's
 * lines, the brief's (todo.week.lines, finished by the backend; the owner's
 * rule: the backend is for business logic, the front end is display): the
 * condition first, in its colour with its icon (a warning mark for a
 * danger), then what to do with the plants named: "Too cold Fri–Tue nights
 * · cover the basil, summer squash, tomatoes and ginger"; the water line
 * last and muted ("Water today · the lettuce and basil"). On a computer the
 * lines sit in two columns. A tap on a line keeps only that line, with
 * "Show all ×" beside it, and the garden below is its plants (the ?plants=
 * and ?for= filter Today's Coming up links use, so the toolbar's chip is
 * left to this line). A line about the garden as a whole (no plants) opens
 * the forecast on Weather instead.
 *
 * Until the brief answers, the bar's room is held: the height the whole bar
 * had last time on this device (the strip line comes with the brief too,
 * and wraps to a second row on a phone), the lines' part of it under the
 * row with "Checking the forecast" in it, so the toolbar and the cards do
 * not move down when the brief lands; before this device has seen the bar,
 * WEEK_BAR_RESERVED_PX for the lines. The row holds its own height before
 * the reading is in.
 *
 * It used to say the week from the cards' marks gathered in the app
 * ("Too cold, Fri, Sat, Sun, Mon and Tue nights · 4 plants"), counting the
 * plants rather than naming them, and the reading, tonight's low and the
 * week's rain in its own row. Before that it was chips, and before that a
 * count of the garden's plants in each state.
 *
 * With the one to-do list (D51) the bar is the weather row alone
 * (weatherOnly): the week is said under it as jobs (PlantJobs), by bed, and
 * the week's lines stay for the filter sheet and the Weather page. (The audit
 * of 2026-10-16 put the lines back; the owner had them out again the same
 * day: "didnt we change this?")
 *
 * Where: PlantGrid, above the toolbar.
 * API: none of its own: the brief (GET /today/brief, useTodayBrief in
 * PlantGrid, which hands the strip and the lines down), the reading (GET
 * /header through useWeatherNow: the reading, the sky's words and its icon
 * key, all the server's; W24 Job D).
 * Settings: WEEK_BAR_HEIGHT_STORAGE_KEY, WEEK_BAR_RESERVED_PX
 * (config/tunables/plantList).
 * Words (en/es): weather.title, weather.bar.status.checking,
 * weather.bar.allClear, weather.bar.showAll; the lines' own words are the
 * brief's.
 * Watch out: a line's "pressed" state is told by the filter's settings
 * (its plants and its title), not kept here, so a reload keeps it.
 *
 * @module GardenConditionsBar
 */

import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, ChevronRight, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { useWeatherNow } from '@/shared/hooks/useWeatherNow';
import { SkyIcon } from '@/shared/components/SkyIcon';
import type { BriefWeekLine } from '@/shared/api/todayBrief';
import { WEEK_BAR_HEIGHT_STORAGE_KEY, WEEK_BAR_RESERVED_PX } from '@/config/tunables/plantList';
import type { AttentionState } from '../lib/plantViews';
import { weekLineIcon, weekLineInk } from '../lib/weekLineLook';

/** What the block shows, and how it narrows the garden below it. */
interface GardenConditionsBarProps {
  /** The week's lines, the brief's (todo.week.lines), in its order: the water line last. */
  lines: BriefWeekLine[];
  /** The brief's strip line under the reading ("Low 67° tonight · no rain in the forecast"), or null until the brief is in. */
  strip: string | null;
  /**
   * Whether the week is known: then the lines (or "all clear") are drawn;
   * checking holds their room; unavailable draws nothing under the weather.
   */
  weekState: AttentionState;
  /** The line the garden below is narrowed to, or null. */
  activeLine: BriefWeekLine | null;
  /** Narrows the garden to a line's plants, or back to all of it when it is already the one. */
  onToggleLine: (line: BriefWeekLine) => void;
  /**
   * The weather row alone: the one to-do list's jobs say the week under it
   * (PlantJobs; D51), so the week's lines are not drawn.
   */
  weatherOnly?: boolean;
}

/** Where a line about the garden as a whole leads: the Weather page, at its forecast (as Today's Coming up links do). */
const FORECAST_LINK = '/weather#forecast';

/** A line's ask as the tappable part: the light underline the plant names on Today wear. */
const ASK_LINK = 'underline decoration-[color-mix(in_srgb,currentColor_45%,transparent)] decoration-1 underline-offset-4';

/** A line's button or link: at least a thumb tall, its words wrapping. */
const LINE = 'block min-h-10 w-full py-1.5 text-left text-sm leading-snug focus:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm';

/** The weather row's least height (its min-h-[4.25rem]), which is its height until the strip line lands. Pixels. */
const ROW_MIN_PX = 68;

/**
 * The room the whole bar took last time on this device, when the window is
 * the same width; null before it has been seen. Storage can be refused or
 * empty.
 */
function rememberedHeight(): number | null {
  try {
    const kept: unknown = JSON.parse(window.localStorage.getItem(WEEK_BAR_HEIGHT_STORAGE_KEY) ?? 'null');
    if (kept && typeof kept === 'object' && (kept as { w?: unknown }).w === window.innerWidth) {
      const h = (kept as { h?: unknown }).h;
      if (typeof h === 'number' && h > 0) return h;
    }
  } catch { /* storage blocked or unreadable */ }
  return null;
}

/**
 * Keeps the room the whole bar takes on this device, for the next load.
 *
 * @param height - The bar's height, px.
 */
function rememberHeight(height: number): void {
  try {
    window.localStorage.setItem(WEEK_BAR_HEIGHT_STORAGE_KEY, JSON.stringify({ w: window.innerWidth, h: height }));
  } catch { /* storage blocked */ }
}

/**
 * The weather, once, above the garden, and what the week holds for it.
 *
 * @param props - GardenConditionsBarProps.
 */
export const GardenConditionsBar: React.FC<GardenConditionsBarProps> = ({
  lines, strip, weekState, activeLine, onToggleLine, weatherOnly = false,
}) => {
  const { reactTranslate: t } = useTranslation();
  // The reading every page shows (useWeatherNow), so this bar and Today agree.
  const now = useWeatherNow();
  const temperature = now?.display ?? null;
  /** The sky in the gardener's language, the server's. */
  const words = now?.words || null;

  /** The bar, measured once the week is known, so its room is held next time; and its weather row. */
  const barRef = useRef<HTMLElement>(null);
  const rowRef = useRef<HTMLAnchorElement>(null);
  useEffect(() => {
    // Only the whole bar's height is worth keeping: a pressed line is one line tall.
    if (weekState !== 'known' || activeLine !== null || !barRef.current) return;
    /** Keeps the bar's height as it stands, if it is still on the page. */
    const measure = (): void => { if (barRef.current) rememberHeight(barRef.current.offsetHeight); };
    measure();
    // Again once the page's fonts are in: a fallback font wraps the lines differently, and the
    // first visit measured a row short.
    if (typeof document !== 'undefined' && document.fonts?.ready) void document.fonts.ready.then(measure);
  }, [weekState, lines, strip, activeLine]);
  /**
   * The room to hold for the lines while the brief is on its way: the bar's
   * remembered height less the row as it stands (the strip's rows come with
   * the brief), else the default. Measured after each commit while checking
   * (the reading landing makes the row taller), before the paint, so the
   * bar never overshoots its remembered height between.
   */
  const [reserved, setReserved] = useState(WEEK_BAR_RESERVED_PX);
  useLayoutEffect(() => {
    if (weekState !== 'checking') return;
    const remembered = rememberedHeight();
    const row = rowRef.current?.offsetHeight ?? ROW_MIN_PX;
    setReserved(remembered !== null && remembered > row ? remembered - row : WEEK_BAR_RESERVED_PX);
  }, [weekState, temperature, words, strip]);

  // Nothing to say and nothing coming: out of the way (no postcode, the archive on screen).
  if (weekState === 'unavailable' && !temperature && !words && !strip) return null;

  /** The lines to draw: the pressed one alone, else all of them. */
  const shown = activeLine ? lines.filter((line) => line.id === activeLine.id) : lines;

  return (
    <section
      ref={barRef}
      aria-label={t('weather.title')}
      className="mb-4 overflow-hidden rounded-lg border border-border bg-card"
    >
      {/* Today's weather row: the sky's key in the app's standard icons (SkyIcon), the reading and the sky's
          words, then the brief's strip line; the whole row opens Weather. Its height is held (min-h) before
          the reading is in, so the lines under it do not move. No label of its own: the section is the
          one named Weather, and a screen reader hears the whole row. */}
      <Link
        ref={rowRef}
        to="/weather"
        className="flex min-h-[4.25rem] items-center gap-2.5 px-3 py-2.5 transition-colors hover:bg-muted/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
      >
        <span className="flex w-10 shrink-0 justify-center">
          {now && <SkyIcon sky={now.icon} className="h-8 w-8 text-muted-foreground" strokeWidth={1.5} aria-hidden="true" />}
        </span>
        <span className="min-w-0 flex-1">
          <span className="flex flex-wrap items-baseline gap-x-2">
            {temperature && <span className="text-2xl font-semibold leading-none">{temperature}</span>}
            {words && <span className="text-sm">{words}</span>}
          </span>
          {strip && <span className="mt-1 block text-sm text-muted-foreground">{strip}</span>}
        </span>
        <ChevronRight className="h-5 w-5 shrink-0 text-muted-foreground" aria-hidden="true" />
      </Link>

      {/* The week's room, held until the brief answers, so the cards below do not move when it lands. */}
      {weekState === 'checking' && !weatherOnly && (
        <div className="border-t px-3 py-1.5" style={{ minHeight: reserved }} aria-busy="true">
          <p className="py-1 text-xs text-muted-foreground">{t('weather.bar.status.checking')}</p>
        </div>
      )}

      {weekState === 'known' && !weatherOnly && (
        <div className="border-t px-3 py-1">
          {lines.length === 0 ? (
            <p className="flex items-center gap-1.5 py-1.5 text-sm text-verdict-good">
              <Check className="h-4 w-4" aria-hidden="true" />
              {t('weather.bar.allClear')}
            </p>
          ) : (
            /* One line per rule line; two columns on a computer, one when a line is pressed. */
            <ul className={cn('grid grid-cols-1 gap-x-6', !activeLine && 'md:grid-cols-2')}>
              {shown.map((line) => {
                /** The line's icon: a warning mark for a danger, else the condition's. */
                const Icon = weekLineIcon(line);
                const ink = weekLineInk(line);
                const pressed = activeLine?.id === line.id;
                /** The line's words: its title in its colour, then the ask, and "Show all" while pressed. */
                const words = (
                  <>
                    <span className={cn('font-semibold', ink)}>
                      <Icon className="mr-1 inline-block h-3.5 w-3.5 align-[-2px]" aria-hidden="true" />
                      {line.title}
                    </span>
                    {line.ask && (
                      <>
                        <span className="text-muted-foreground" aria-hidden="true"> · </span>
                        {/* The ask is the tappable part: underlined lightly, muted for water, plain while pressed. */}
                        <span className={cn(line.water ? 'text-muted-foreground' : 'text-foreground', !pressed && ASK_LINK)}>{line.ask}</span>
                      </>
                    )}
                    {pressed && (
                      <span className="ml-2.5 inline-flex items-center gap-0.5 whitespace-nowrap text-foreground underline decoration-1 underline-offset-4">
                        {t('weather.bar.showAll')}<X className="h-3.5 w-3.5" aria-hidden="true" />
                      </span>
                    )}
                  </>
                );
                return (
                  <li key={line.id}>
                    {line.plantIds.length > 0 ? (
                      <button type="button" onClick={() => onToggleLine(line)} aria-pressed={pressed} className={LINE}>
                        {words}
                      </button>
                    ) : (
                      /* A line about the garden as a whole: the forecast, on Weather. */
                      <Link to={FORECAST_LINK} className={LINE}>{words}</Link>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </section>
  );
};
